/* The engine's side of the page: what runs inside the Web Worker.
 *
 * **The tester's one bundle is loaded twice, once by the page and once as a
 * worker made from its own URL**, and this is the half the worker runs. The
 * owner's requirement, 2026-09-13: a compile must never block typing. Every
 * engine call is synchronous WebAssembly, and a large pattern over a long
 * subject is three compiles and a full scan per keystroke, which on the page's
 * thread is a caret that stops moving.
 *
 * **`handle` is a plain function from a request to a reply**, and `serve` is
 * the two lines that tie it to a worker scope. The split is what lets the
 * wiring harness run the REAL bundle on both sides under node, where a worker
 * is a thread with a different message API, and what keeps the protocol in one
 * readable place:
 *
 *   { id, kind: "analyse", pattern, subject, flags }
 *     -> { id, found, items }                the match and the enumeration
 *     -> { id, found: { error, at } }        a pattern PCRE2 refused
 *   { id, kind: "trace", pattern, subject, flags }
 *     -> { id, ...trace }                    the step debugger's run
 *   the worker announces itself once: { ready: true, version }
 *
 * **One analyse is one message.** The tester used to compile the pattern three
 * times per keystroke: a match, an enumeration for the summary's part count,
 * and the same enumeration again for the diagram. Crossing a thread boundary
 * made the duplication visible, so the enumeration now happens once and both
 * readers share it.
 */

import { start, match, enumerate, trace, version } from "./engine.js";

export async function handle(request) {
  await start();

  try {
    switch (request.kind) {
      case "analyse":
        return analyse(request);
      case "trace":
        return { id: request.id, ...trace(request.pattern, request.subject, request.flags) };
      default:
        return { id: request.id, error: `unknown request ${JSON.stringify(request.kind)}` };
    }
  } catch (failure) {
    // A thrown exception here is a defect rather than a pattern problem, and
    // an unanswered request would leave the page waiting on it forever.
    return { id: request.id, error: String(failure && failure.message ? failure.message : failure) };
  }
}

function analyse({ id, pattern, subject, flags }) {
  const found = match(pattern, subject, flags);

  if (found.error) {
    return { id, found };
  }

  return { id, found, items: enumerate(pattern, flags) };
}

/**
 * Wire `handle` to a worker scope. The engine is started before the page is
 * told the worker is ready, so the first keystroke never waits on
 * instantiation. **A failure to start is POSTED, not thrown**: a rejection
 * inside a worker does not reach the page's `onerror`, so throwing would leave
 * the page waiting on a worker that never answers.
 */
export function serve(scope) {
  scope.onmessage = (event) => {
    handle(event.data).then((reply) => scope.postMessage(reply));
  };

  start().then(
    () => scope.postMessage({ ready: true, version: version() }),
    (failure) => scope.postMessage({ ready: false, error: String(failure) }));
}
